/**
 * MenuSection.tsx - Menu Section Component
 * 
 * Groups menu items under a category header with optional collapsing
 * Migrated from Interfacr SideMenuSection with enhancements
 * 
 * FEATURES:
 * - Category headers
 * - Collapsible sections
 * - Drag and drop support
 * - Action logging (Actr)
 * - Multiple MenuItems
 * 
 * ARCHITECTURE:
 * - Uses MenuItem component
 * - Uses @opcentral/actr for action logging
 * 
 * CHANGE HISTORY:
 * - 251115_1710 [current] danielbeer: Created with orchestrator integration
 */

import React, { useCallback, useMemo, useState } from 'react';
import { MenuItem } from './MenuItem';
import type { MenuSectionProps, MenuItem as MenuItemType } from './types';

export const MenuSection: React.FC<MenuSectionProps> = ({
  title,
  items,
  collapsed = false,
  expandedItems = [],
  selectedItem,
  draggable = false,
  onItemClick,
  onToggleExpanded,
  onItemReorder,
  className = ''
}) => {
  const [sectionCollapsed, setSectionCollapsed] = useState(false);
  
  // Don't render empty sections
  if (items.length === 0) {
    return null;
  }
  
  // Handle section header click to collapse/expand
  const handleSectionToggle = useCallback(() => {
    const newCollapsed = !sectionCollapsed;
    setSectionCollapsed(newCollapsed);
    
    actr?.execute('menu:section-toggle', {
      title,
      collapsed: newCollapsed,
      itemCount: items.length
    });
  }, [sectionCollapsed, title, items.length, actr]);
  
  // Handle item click
  const handleItemClick = useCallback((item: MenuItemType) => {
    actr?.execute('menu:section-item-click', {
      section: title,
      itemId: item.id,
      label: item.label
    });
    
    onItemClick(item);
  }, [title, actr, onItemClick]);
  
  // Section classes
  const sectionClasses = useMemo(() => [
    'menu-section',
    sectionCollapsed ? 'menu-section--collapsed' : '',
    collapsed ? 'menu-section--menu-collapsed' : '',
    className
  ].filter(Boolean).join(' '), [sectionCollapsed, collapsed, className]);
  
  return (
    <div className={sectionClasses}>
      {/* Section header */}
      {title && !collapsed && (
        <div 
          className="menu-section__header"
          onClick={handleSectionToggle}
          role="button"
          tabIndex={0}
          aria-expanded={!sectionCollapsed}
          aria-label={`${title} section`}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleSectionToggle();
            }
          }}
        >
          <span className="menu-section__title">{title}</span>
          <span className="menu-section__toggle" aria-hidden="true">
            {sectionCollapsed ? '▸' : '▾'}
          </span>
          <span className="menu-section__count" aria-label={`${items.length} items`}>
            ({items.length})
          </span>
        </div>
      )}
      
      {/* Section items */}
      {!sectionCollapsed && (
        <div 
          className="menu-section__items"
          role="group"
          aria-label={title ? `${title} items` : 'Menu items'}
        >
          {items.map((item, index) => (
            <MenuItem
              key={item.id}
              item={item}
              index={index}
              collapsed={collapsed}
              selected={selectedItem === item.id}
              expanded={expandedItems.includes(item.id)}
              draggable={draggable}
              onClick={() => handleItemClick(item)}
              onToggleExpanded={() => onToggleExpanded?.(item.id)}
              onReorder={onItemReorder}
            />
          ))}
        </div>
      )}
    </div>
  );
};

MenuSection.displayName = 'MenuSection';
